import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, MessageSquare, Mail, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Three-button row that lets an admin (1) download an order receipt as PDF,
 * (2) text the customer the PDF link via Twilio SMS, and (3) email them
 * the PDF as an attachment via Resend.
 *
 * Works against any order saved in mtm_orders / pos_orders / orders — the
 * backend resolves the id across all three collections.
 *
 * If `ensureSavedOrderId` is provided, it is called before every action and
 * the returned id is used — this lets pages auto-save the order on first
 * click (e.g. Quick Order, POS) without having to expose a separate Save CTA.
 *
 * Props:
 *   orderId            UUID of an already-saved order (optional if ensureSavedOrderId is set)
 *   orderNumber        human-readable order number (used in dialogs)
 *   defaultPhone       pre-fills the SMS dialog
 *   defaultEmail       pre-fills the Email dialog
 *   ensureSavedOrderId async () => string — return the id of a freshly saved order, or null on failure
 *   variant            "row"   → compact icon-only buttons (table rows)
 *                      "stack" → full-width stacked buttons (confirmation dialogs)
 *                      "inline" (default) → labelled side-by-side buttons
 */
export default function OrderActions({
  orderId,
  orderNumber = "",
  defaultPhone = "",
  defaultEmail = "",
  ensureSavedOrderId,
  variant = "inline",
  className = "",
  testIdPrefix = "order-actions",
}) {
  const [downloading, setDownloading] = useState(false);
  const [smsOpen, setSmsOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [phone, setPhone] = useState(defaultPhone || "");
  const [email, setEmail] = useState(defaultEmail || "");
  const [customMessage, setCustomMessage] = useState("");
  const [customSubject, setCustomSubject] = useState("");
  // Template toggle — "factory" is the new RC-Factory-style PDF (default);
  // "legacy" falls back to the original branded receipt for A/B comparison.
  const [pdfTemplate, setPdfTemplate] = useState("factory");

  // Resolve the active order id — uses the prop or asks the parent to save first.
  const resolveOrderId = async () => {
    if (orderId) return orderId;
    if (ensureSavedOrderId) {
      try {
        const id = await ensureSavedOrderId();
        if (id) return id;
      } catch (e) {
        console.error("ensureSavedOrderId failed", e);
      }
      toast.error("Couldn't save the order — please review the form and try again.");
      return null;
    }
    toast.error("This order has not been saved yet.");
    return null;
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const id = await resolveOrderId();
      if (!id) return;
      const res = await axios.get(`${API}/orders/${id}/pdf`, {
        params: { template: pdfTemplate },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `laguerre-order-${orderNumber || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("PDF saved");
    } catch (e) {
      console.error("PDF download failed", e);
      toast.error(e?.response?.data?.detail || "Failed to download PDF");
    } finally {
      setDownloading(false);
    }
  };

  const openSmsDialog = async () => {
    const id = await resolveOrderId();
    if (!id) return;
    setPhone(defaultPhone || phone);
    setSmsOpen(true);
  };

  const openEmailDialog = async () => {
    const id = await resolveOrderId();
    if (!id) return;
    setEmail(defaultEmail || email);
    setEmailOpen(true);
  };

  const sendSms = async () => {
    if (!phone.trim()) {
      toast.error("Enter a phone number to send to.");
      return;
    }
    setSending(true);
    try {
      const id = await resolveOrderId();
      if (!id) return;
      const res = await axios.post(`${API}/orders/${id}/send-sms`, {
        phone: phone.trim(),
        message: customMessage.trim() || null,
        template: pdfTemplate,
      });
      toast.success(`SMS sent to ${res.data.to}`);
      setSmsOpen(false);
      setCustomMessage("");
    } catch (e) {
      console.error("SMS send failed", e);
      const detail = e?.response?.data?.detail || "Failed to send SMS";
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  const sendEmail = async () => {
    if (!email.trim()) {
      toast.error("Enter an email address.");
      return;
    }
    setSending(true);
    try {
      const id = await resolveOrderId();
      if (!id) return;
      const res = await axios.post(`${API}/orders/${id}/send-email`, {
        email: email.trim(),
        subject: customSubject.trim() || null,
        message: customMessage.trim() || null,
        template: pdfTemplate,
      });
      toast.success(`Email sent to ${res.data.to}`);
      setEmailOpen(false);
      setCustomMessage("");
      setCustomSubject("");
    } catch (e) {
      console.error("Email send failed", e);
      const detail = e?.response?.data?.detail || "Failed to send email";
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  const Btn = ({ icon: Icon, label, onClick, loading, testId, tone = "default" }) => {
    if (variant === "row") {
      const toneClasses = {
        accent: "hover:bg-amber-500/10 text-amber-400",
        sky: "hover:bg-sky-500/10 text-sky-400",
        emerald: "hover:bg-emerald-500/10 text-emerald-400",
      };
      return (
        <Button
          variant="ghost"
          size="sm"
          className={toneClasses[tone] || toneClasses.sky}
          onClick={onClick}
          disabled={loading}
          data-testid={testId}
          title={label}
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Icon size={14} />}
        </Button>
      );
    }
    if (variant === "stack") {
      return (
        <Button
          variant="outline"
          className="w-full"
          onClick={onClick}
          disabled={loading}
          data-testid={testId}
        >
          {loading ? (
            <Loader2 size={16} className="mr-2 animate-spin" />
          ) : (
            <Icon size={16} className="mr-2" />
          )}
          {label}
        </Button>
      );
    }
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={onClick}
        disabled={loading}
        data-testid={testId}
      >
        {loading ? (
          <Loader2 size={14} className="mr-1.5 animate-spin" />
        ) : (
          <Icon size={14} className="mr-1.5" />
        )}
        {label}
      </Button>
    );
  };

  const containerClass =
    variant === "stack"
      ? "space-y-2"
      : variant === "row"
      ? "inline-flex items-center gap-1"
      : "inline-flex items-center gap-2 flex-wrap";

  return (
    <>
      <div className={`${containerClass} ${className}`} data-testid={testIdPrefix}>
        {/* Template toggle — segmented control, hidden in "row" mode to keep
            table-row triggers compact. */}
        {variant !== "row" && (
          <div
            className="inline-flex items-center rounded-md border border-white/10 bg-[#0a1628] p-0.5 text-[10px]"
            data-testid={`${testIdPrefix}-template-toggle`}
          >
            <button
              type="button"
              onClick={() => setPdfTemplate("factory")}
              data-testid={`${testIdPrefix}-template-factory`}
              className={`px-2 py-1 rounded transition ${
                pdfTemplate === "factory"
                  ? "bg-amber-500/20 text-amber-300 font-semibold"
                  : "text-slate-400 hover:text-white"
              }`}
              title="RC Factory-style PDF (new)"
            >
              Factory
            </button>
            <button
              type="button"
              onClick={() => setPdfTemplate("legacy")}
              data-testid={`${testIdPrefix}-template-legacy`}
              className={`px-2 py-1 rounded transition ${
                pdfTemplate === "legacy"
                  ? "bg-sky-500/20 text-sky-300 font-semibold"
                  : "text-slate-400 hover:text-white"
              }`}
              title="Legacy Laguerre branded receipt"
            >
              Legacy
            </button>
          </div>
        )}
        <Btn
          icon={Download}
          label="Save as PDF"
          onClick={downloadPdf}
          loading={downloading}
          testId={`${testIdPrefix}-pdf-btn`}
          tone="accent"
        />
        <Btn
          icon={MessageSquare}
          label="Send via SMS"
          onClick={openSmsDialog}
          loading={false}
          testId={`${testIdPrefix}-sms-btn`}
          tone="sky"
        />
        <Btn
          icon={Mail}
          label="Send via Email"
          onClick={openEmailDialog}
          loading={false}
          testId={`${testIdPrefix}-email-btn`}
          tone="emerald"
        />
      </div>

      {/* SMS Dialog */}
      <Dialog open={smsOpen} onOpenChange={setSmsOpen}>
        <DialogContent className="max-w-md" data-testid={`${testIdPrefix}-sms-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare size={18} /> Text Order to Customer
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">
                Recipient phone
              </label>
              <Input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 415 555 0123"
                data-testid={`${testIdPrefix}-sms-phone`}
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                US 10-digit numbers are auto-prefixed with +1. Other countries
                require the leading <span className="font-mono">+</span> and country code.
              </p>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">
                Custom opening line <span className="text-muted-foreground/60">(optional)</span>
              </label>
              <Textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                placeholder="Leave blank for the default Laguerre greeting"
                rows={2}
                data-testid={`${testIdPrefix}-sms-message`}
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                A short summary and a link to the order PDF will be appended
                automatically.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSmsOpen(false)}
              disabled={sending}
              data-testid={`${testIdPrefix}-sms-cancel`}
            >
              Cancel
            </Button>
            <Button
              onClick={sendSms}
              disabled={sending || !phone.trim()}
              data-testid={`${testIdPrefix}-sms-send`}
            >
              {sending ? (
                <>
                  <Loader2 size={14} className="mr-2 animate-spin" /> Sending…
                </>
              ) : (
                <>
                  <MessageSquare size={14} className="mr-2" /> Send Text
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="max-w-md" data-testid={`${testIdPrefix}-email-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail size={18} /> Email Order to Customer
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">
                Recipient email
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="customer@example.com"
                data-testid={`${testIdPrefix}-email-to`}
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">
                Custom subject <span className="text-muted-foreground/60">(optional)</span>
              </label>
              <Input
                value={customSubject}
                onChange={(e) => setCustomSubject(e.target.value)}
                placeholder={`Laguerre — Your order ${orderNumber}`}
                data-testid={`${testIdPrefix}-email-subject`}
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">
                Personal note <span className="text-muted-foreground/60">(optional)</span>
              </label>
              <Textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                placeholder="Leave blank for the default Laguerre note"
                rows={3}
                data-testid={`${testIdPrefix}-email-message`}
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                The PDF spec sheet is attached to the email automatically.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEmailOpen(false)}
              disabled={sending}
              data-testid={`${testIdPrefix}-email-cancel`}
            >
              Cancel
            </Button>
            <Button
              onClick={sendEmail}
              disabled={sending || !email.trim()}
              data-testid={`${testIdPrefix}-email-send`}
            >
              {sending ? (
                <>
                  <Loader2 size={14} className="mr-2 animate-spin" /> Sending…
                </>
              ) : (
                <>
                  <Mail size={14} className="mr-2" /> Send Email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
